import {
  OPERATOR_LABEL,
  type WhereDataset,
  type WhereField,
  type WhereGroup,
  type WhereOperator,
  type WhereRule,
  describeWhere,
  operatorsFor,
  safeParseWhere,
  whereField,
  whereFieldName,
} from '@parcel/filters';
import { Button, Popover, PopoverAnchor, PopoverContent } from '@parcel/ui';
// `Filter` is lucide's funnel glyph (renamed `Funnel` in later lucide versions).
import { Braces, Filter } from 'lucide-react';
import * as React from 'react';
import { Chip } from '../filter-bar/filter-chips.js';
import { RuleValueEditor } from './rule-value-editor.js';
import { fieldTiers } from './tier-shortcuts.js';
import { type WhereOptions, fieldValues } from './where-menu.js';

/** A rule with its value checked against the field list, or null while it is incomplete. */
export function validRule(dataset: WhereDataset, rule: WhereRule): WhereRule | null {
  const out = safeParseWhere(dataset, { combinator: 'and', rules: [rule] });
  return out.ok ? (out.where.rules[0] as WhereRule) : null;
}

/** A sensible value when the operator changes: keep it if the shape still fits, else start empty. */
export function valueForOperator(
  field: WhereField,
  from: string,
  to: string,
  value: unknown,
): unknown {
  if (to === 'null' || to === 'notNull') return undefined;
  const lists = new Set(['in', 'notIn']);
  if (lists.has(from) && lists.has(to)) return value;
  const singles = new Set(['=', '!=', '>', '<']);
  if (singles.has(from) && singles.has(to)) return value;
  const ago = new Set(['withinLast', 'notWithinLast']);
  if (ago.has(from) && ago.has(to)) return value;
  if (field.kind === 'yes_no') return value;
  return undefined;
}

const dateText = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

function numberText(field: WhereField | undefined, n: unknown): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n);
  if (field?.unit === 'year') return String(v);
  const text = v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return field?.unit === 'usd' ? `$${text}` : text;
}

/** The value pills a chip shows: option labels, "50 to 200", "3 months ago", "Yes"; none for empty checks. */
export function chipValues(
  dataset: WhereDataset,
  rule: WhereRule,
  options: WhereOptions,
): Array<{ value: string; label: string }> {
  const field = whereField(dataset, rule.field);
  if (!field || rule.operator === 'null' || rule.operator === 'notNull') return [];
  const v = rule.value;
  if (Array.isArray(v) && rule.operator === 'between') {
    if (field.kind === 'date')
      return [{ value: 'range', label: `${dateText(String(v[0]))} to ${dateText(String(v[1]))}` }];
    if (field.kind === 'score') {
      const tier = fieldTiers(field, options).find((t) => t.value === `${v[0]}-${v[1]}`);
      if (tier) return [{ value: 'range', label: tier.label }];
    }
    return [{ value: 'range', label: `${numberText(field, v[0])} to ${numberText(field, v[1])}` }];
  }
  if (Array.isArray(v)) {
    const byValue = new Map(fieldValues(field, options).map((o) => [o.value, o.label]));
    return (v as string[]).map((x) => ({ value: x, label: byValue.get(x) ?? x }));
  }
  if (typeof v === 'boolean') return [{ value: 'bool', label: v ? 'Yes' : 'No' }];
  if (typeof v === 'number') return [{ value: 'n', label: numberText(field, v) }];
  if (typeof v === 'string') return [{ value: 's', label: `“${v}”` }];
  if (v && typeof v === 'object') {
    const { amount, unit } = v as { amount: number; unit: string };
    return [
      { value: 'ago', label: `${amount} ${amount === 1 ? unit.replace(/s$/, '') : unit} ago` },
    ];
  }
  return [];
}

/** The operator word a chip shows between its name and values; "is" and "between" stay implied ("50 to 200", "Tier 1"). */
export function chipOperator(rule: WhereRule): string | null {
  if (rule.operator === 'in' || rule.operator === '=' || rule.operator === 'between') return null;
  return OPERATOR_LABEL[rule.operator as WhereOperator] ?? rule.operator;
}

interface RuleEditorPanelProps {
  dataset: WhereDataset;
  rule: WhereRule;
  options: WhereOptions;
  onCommit: (rule: WhereRule) => void;
  onRemove: () => void;
  onDone: () => void;
  /** An existing chip whose last list value was unticked; a new one just waits. */
  onEmpty?: () => void;
}

/** The chip's popover: a small operator picker above the value control. */
export function RuleEditorPanel({
  dataset,
  rule,
  options,
  onCommit,
  onRemove,
  onDone,
  onEmpty,
}: RuleEditorPanelProps) {
  const field = whereField(dataset, rule.field);
  const [draft, setDraft] = React.useState<WhereRule>(rule);
  if (!field) return null;
  const operators = operatorsFor(field);
  const valid = validRule(dataset, draft);

  // List picks, yes/no and the empty checks are whole on each click; typed values wait for Apply.
  const savesOnChange = (operator: string) =>
    operator === 'null' ||
    operator === 'notNull' ||
    field.kind === 'yes_no' ||
    (field.kind === 'choice' && operator !== 'between');

  const update = (next: WhereRule) => {
    setDraft(next);
    if (!savesOnChange(next.operator)) return;
    const checked = validRule(dataset, next);
    if (checked) onCommit(checked);
    else if (Array.isArray(next.value) && next.value.length === 0) onEmpty?.();
  };

  const apply = () => {
    if (!valid) return;
    onCommit(valid);
    onDone();
  };

  return (
    <div className="flex min-w-[260px] flex-col">
      <div className="flex items-center gap-2 border-b px-2 py-1.5 text-xs">
        <span className="truncate font-medium">{whereFieldName(dataset, field.key)}</span>
        {operators.length > 1 ? (
          <select
            aria-label="Operator"
            value={draft.operator}
            onChange={(e) => {
              const operator = e.target.value;
              update({
                field: field.key,
                operator,
                value: valueForOperator(field, draft.operator, operator, draft.value),
              });
            }}
            className="ml-auto h-7 rounded-md border bg-background px-1.5 text-xs"
          >
            {operators.map((op) => (
              <option key={op} value={op}>
                {field.kind === 'date' && (op === 'withinLast' || op === 'notWithinLast')
                  ? `${OPERATOR_LABEL[op]} … ago`
                  : OPERATOR_LABEL[op]}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <RuleValueEditor
        dataset={dataset}
        field={field}
        operator={draft.operator}
        value={draft.value}
        options={options}
        onChange={(value) => update({ ...draft, value })}
        onSubmit={apply}
      />
      <div className="flex items-center justify-between border-t p-1">
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onRemove}>
          Remove filter
        </Button>
        {savesOnChange(draft.operator) ? (
          <Button variant="ghost" size="sm" onClick={onDone}>
            Done
          </Button>
        ) : (
          <Button size="sm" disabled={!valid} onClick={apply}>
            Apply
          </Button>
        )}
      </div>
    </div>
  );
}

interface WhereRuleChipProps {
  dataset: WhereDataset;
  rule: WhereRule;
  options: WhereOptions;
  chipKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommit: (rule: WhereRule) => void;
  onRemove: () => void;
}

/** One top-level rule as today's chip, with its editor in a popover. */
export function WhereRuleChip({
  dataset,
  rule,
  options,
  chipKey,
  open,
  onOpenChange,
  onCommit,
  onRemove,
}: WhereRuleChipProps) {
  const name = whereFieldName(dataset, rule.field);
  const operator = chipOperator(rule);
  // The anchor sits outside the portaled content, so a click on this chip must not count as "outside".
  const guardOwnChipDismiss = (event: {
    target: EventTarget | null;
    preventDefault: () => void;
  }) => {
    const target = event.target as Element | null;
    if (target?.closest('[data-chip-key]')?.getAttribute('data-chip-key') === chipKey)
      event.preventDefault();
  };
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <Chip
          data-chip-key={chipKey}
          icon={<Filter className="h-3 w-3 text-muted-foreground" aria-hidden />}
          name={name}
          operator={operator ?? undefined}
          values={chipValues(dataset, rule, options)}
          onName={() => onOpenChange(!open)}
          onRemove={onRemove}
        />
      </PopoverAnchor>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onPointerDownOutside={guardOwnChipDismiss}
        onFocusOutside={guardOwnChipDismiss}
      >
        <RuleEditorPanel
          dataset={dataset}
          rule={rule}
          options={options}
          onCommit={onCommit}
          onRemove={onRemove}
          onDone={() => onOpenChange(false)}
          onEmpty={onRemove}
        />
      </PopoverContent>
    </Popover>
  );
}

/** A group chip's name: "not" over "any of" is "None of"; over "all of" it is "Not all of". */
export function groupName(group: Pick<WhereGroup, 'combinator' | 'not'>): string {
  if (group.combinator === 'or') return group.not ? 'None of' : 'Any of';
  return group.not ? 'Not all of' : 'All of';
}

/** A top-level group (OR, written by Ask, Spec or the API): one removable chip, read-only here. */
export function WhereGroupChip({
  dataset,
  group,
  labels,
  onRemove,
}: {
  dataset: WhereDataset;
  group: WhereGroup;
  labels?: Parameters<typeof describeWhere>[2];
  onRemove: () => void;
}) {
  const count = group.rules.length;
  return (
    <Chip
      title={describeWhere(dataset, group, labels)}
      icon={<Braces className="h-3 w-3 text-muted-foreground" aria-hidden />}
      name={groupName(group)}
      values={[{ value: 'n', label: `${count} ${count === 1 ? 'condition' : 'conditions'}` }]}
      onName={() => {}}
      onRemove={onRemove}
      className="shadow-[0_0_0_1px_hsl(var(--foreground))]"
    />
  );
}
